import { addToast, Button, showDialogConfirm, Table } from '@octano/global-ui';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationsKeys } from '../../../../locales/translations';
import useActions from '../hooks/useActions';
import useColumnActions from '../hooks/useColumnActions';
import useColumns from '../hooks/useColumns';
import { AdditionalCharge } from '../interfaces/types';
import AdditionalChargeModal, {
  AdditionalChargeModalMethods,
} from './AdditionalChargeModal';
import { useAdditionalChargesLoader } from './Loaders/AdditionalChargesLoader';

interface AdditionalChargesTableProps {
  type: 'INICIO' | 'CONTINUIDAD';
  title: string;
  subtitle: string;
}

function AdditionalChargesTable({
  type,
  title,
  subtitle,
}: AdditionalChargesTableProps) {
  const { t } = useTranslation(
    TranslationsKeys.CONFIGURATION_ADDITIONAL_CHARGES,
  );
  const modalRef = useRef<AdditionalChargeModalMethods>(null);
  const { data, loading, refresh } = useAdditionalChargesLoader();

  const [deleting, setDeleting] = useState(false);

  const { create, update, destroy } = useActions({
    onError: (key) => {
      setDeleting(false);
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`modal.${key}.failed`),
      });
    },
    onSuccess: (key) => {
      setDeleting(false);
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`modal.${key}.success`),
      });
      modalRef.current?.close();
      refresh();
    },
  });

  const onClickAdd = () => {
    modalRef.current?.open();
  };

  const onClickEdit = (row: AdditionalCharge) => {
    modalRef.current?.open(row);
  };

  const onClickDelete = useCallback(
    (row: AdditionalCharge) => {
      showDialogConfirm({
        icon: {
          name: 'warning',
          color: 'warning',
        },
        title: t('modal.delete.title'),
        subtitle: t('modal.delete.description'),
        btnConfirm: {
          text: t('modal.delete.confirm'),
          onConfirm: () => {
            setDeleting(true);
            destroy(row.id);
          },
        },
        btnClose: {
          text: t('modal.delete.cancel'),
        },
      });
    },
    [t, destroy],
  );

  const columnsActions = useColumnActions({
    onClickEdit,
    onClickDelete,
  });

  const columns = useColumns(columnsActions);

  return (
    <>
      <div className="mb-4">
        <h2 className="text-primary fs-16 fw-600 text-uppercase">{title}</h2>
        <p className="fs-14 text-medium mb-3">{subtitle}</p>
        <div className="d-flex w-100 justify-content-end mb-2">
          <Button
            type="button"
            size="sm"
            icon="plus"
            text={t('addItem')}
            onClick={onClickAdd}
          />
        </div>

        <Table
          columns={columns}
          data={data ?? []}
          isLoadingResults={loading || deleting}
          noResultsText={t('table.noResults')}
        />
      </div>
      <AdditionalChargeModal
        ref={modalRef}
        type={type}
        create={create}
        update={update}
      />
    </>
  );
}

export default AdditionalChargesTable;
